import React, { useEffect, useState, useRef } from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link, useNavigate} from "react-router-dom";
import {useFormik} from "formik";
import {Alert, CircularProgress} from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import Header from "../components/Header";
import TextBox from "../components/TextBox";
import {hostSignup} from "../store/thunks/hostActions";
import {hostSignupReset} from "../store/slices/hostSlices";
import {registrationSchema} from "../schemas";
import logo from "../icons/BellhoptLogoMedRes.png";

const propertyManagementOptions = [
    {label: "Streamline", value: "streamline"},
    {label: "Guesty", value: "guesty"},
    {label: "Other", value: "other"},
];

const SignupScreen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [signupError, setSignupError] = useState(null);
    const [captchaValue, setCaptchaValue] = useState(null);
    const recaptchaRef = useRef(null);

    const onCaptchaChange = (token) => {
        setCaptchaValue(token);
    };

    const onCaptchaExpired = () => {
        setCaptchaValue(null);
        recaptchaRef.current?.reset();
    };

    const initialValues = {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
        isManager: "",
        propertyManagementSoftware: "",
        selfOnboarding: "",
        address: {
            line1: "",
            line2: "",
            city: "",
            state: "",
            zipCode: "",
        },
    };

    const {values, errors, touched, handleBlur, handleChange, getIn} =
        useFormik({
            initialValues: initialValues,
            validationSchema: registrationSchema,
        });

    const {loading, success, error} = useSelector((state) => {
        return state.hostSignUp;
    });
    const {hostInfo} = useSelector((state) => {
        return state.host;
    });

    const [confirmPassword, setConfirmPassword] = useState("");

    const handleSignUp = () => {
        if (!captchaValue) {
            setSignupError("Please verify that you are not a robot.");
            return;
        }


        if (errors.address && Object.keys(errors.address).length > 0) {
            console.log("errors", errors.address);
            setSignupError(errors.address[Object.keys(errors.address)[0]]);

            return;
        }

        if (Object.keys(errors).length > 0) {
            console.log("errors", errors.address);
            setSignupError(errors[Object.keys(errors)[0]]);

            return;
        }

        if (Object.keys(errors).length === 0 && values.password === confirmPassword && captchaValue) {
            // Include the captchaValue when dispatching
            // console.log(captchaValue)
            dispatch(hostSignup({...values, captchaResponse: captchaValue}));
            // console.log(captchaValue)
        }

    };

    useEffect(() => {
        if (hostInfo) {
            navigate("/host");
        }
        if (success) {
            navigate("/please-verify");
            dispatch(hostSignupReset());
        }
        if (error) {
            setTimeout(() => {
                dispatch(hostSignupReset());
            }, 2000);
        }
    }, [success, navigate, hostInfo, error]);

    useEffect(() => {
        setTimeout(() => {
            signupError && setSignupError(null);
        }, 3000);
    }, [signupError]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-500/20 to-primary-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse delay-500" />
            </div>

            {/* Floating Particles */}
            <div className="absolute inset-0">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-white/30 rounded-full animate-pulse"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${2 + Math.random() * 3}s`,
                        }}
                    />
                ))}
            </div>

            <Header />

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        position: "fixed",
                        width: "90%",
                        margin: "auto",
                        top: 20,
                        left: 0,
                        right: 0,
                        zIndex: 10,
                    }}
                >
                    <strong>{error}</strong>
                </Alert>
            )}
            {signupError && (
                <Alert
                    severity="error"
                    sx={{
                        position: "fixed",
                        width: "90%",
                        margin: "auto",
                        top: 20,
                        left: 0,
                        right: 0,
                        zIndex: 10,
                    }}
                >
                    {signupError}
                </Alert>
            )}

            <div className="relative z-10 flex-1 flex justify-center px-4 sm:px-6 lg:px-8 py-12 overflow-y-auto">
                <div className="max-w-md w-full">
                    {/* Hero Section */}
                    <div className="text-center mb-8 animate-fade-in-up">
                        <div className="relative inline-block mb-6">
                            <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-purple-500 rounded-full blur-lg opacity-30 animate-pulse" />
                            <div className="relative bg-white/10 backdrop-blur-sm rounded-full p-6 border border-white/20">
                                <img src={logo} alt="Bellhopt Logo" className="h-28 w-auto mx-auto" />
                            </div>
                        </div>
                        <h1 className="text-4xl font-bold text-black mb-4">Create your account</h1>
                        <p className="text-black text-xl font-medium">Join Bellhopt and start managing your properties</p>
                        <div className="mt-4 flex items-center justify-center space-x-2">
                            <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" />
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-100" />
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce delay-200" />
                        </div>
                    </div>

                    {/* Sign Up Form Card */}
                    <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20 animate-fade-in-scale">
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign Up</h2>
                            <p className="text-gray-700">
                                Already have an account?{" "}
                                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                                    Sign In
                                </Link>
                            </p>
                        </div>

                        <div className="form space-y-4">
                            <div className="flex items-center gap-x-2">
                                <TextBox
                                    label={"First Name"}
                                    name={"firstName"}
                                    value={values.firstName}
                                    onChange={handleChange}
                                    errors={errors}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"Last Name"}
                                    name={"lastName"}
                                    value={values.lastName}
                                    onChange={handleChange}
                                    errors={errors}
                                    handleBlur={handleBlur}
                                />
                            </div>

                            <TextBox label="Email" name="email" value={values.email} onChange={handleChange}
                                     errors={errors} handleBlur={handleBlur}/>
                            <TextBox label="Mobile Number" name="phoneNumber" value={values.phoneNumber}
                                     onChange={handleChange} errors={errors} handleBlur={handleBlur}/>

                            <div className="flex justify-between py-5 items-center gap-4 flex-wrap">
                                <label htmlFor="isManager" className="text-gray-900 font-medium">Are you a Property Manager?</label>
                                <div className="flex">
                                    <label className="mx-2 text-gray-800 cursor-pointer">
                                        <input type="radio" name="isManager" value="true" onChange={handleChange}
                                               onBlur={handleBlur}/> Yes
                                    </label>
                                    <label className="mx-2 text-gray-800 cursor-pointer">
                                        <input type="radio" name="isManager" value="false" onChange={handleChange}
                                               onBlur={handleBlur}/> No
                                    </label>
                                </div>
                            </div>

                            <div className="w-full py-5">
                                <label className="block text-left mb-2 text-gray-900 font-medium">Which property management software are you
                                    using?</label>
                                <div className="flex flex-wrap gap-6">
                                    {propertyManagementOptions.map((option) => (
                                        <label key={option.value} className="flex items-center text-gray-800 cursor-pointer">
                                            <input
                                                className="mr-2"
                                                type="radio"
                                                name="propertyManagementSoftware"
                                                value={option.value}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                checked={values.propertyManagementSoftware === option.value}
                                            />
                                            {option.label}
                                        </label>
                                    ))}
                                </div>
                                {touched.propertyManagementSoftware && errors.propertyManagementSoftware && (
                                    <p className="text-[red] mt-1">{errors.propertyManagementSoftware}</p>
                                )}
                            </div>

                            {/* Radio Buttons For isManager state */}

                            <div className="w-full mobile:w-full">
                                <TextBox
                                    label={"Company Name"}
                                    name={"companyName"}
                                    type={"text"}
                                    value={values.companyName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    errors={errors}

                                />
                            </div>


                            <div className="flex items-center gap-x-7 mobile:flex-col">
                                <TextBox
                                    label={"Address Line 1"}
                                    name={"address.line1"}
                                    type={"text"}
                                    value={values.address.line1}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />

                                <TextBox
                                    label={"Address Line 2"}
                                    name={"address.line2"}
                                    type={"text"}
                                    value={values.address.line2}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                            </div>
                            <div className="flex items-center gap-x-7 mobile:flex-col">
                                <TextBox
                                    label={"City"}
                                    name={"address.city"}
                                    type={"text"}
                                    onChange={handleChange}
                                    value={values.address.city}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"State"}
                                    name={"address.state"}
                                    type={"text"}
                                    value={values.address.state}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                                <TextBox
                                    label={"Zip Code"}
                                    name={"address.zipCode"}
                                    type={"text"}
                                    value={values.address.zipCode}
                                    onChange={handleChange}
                                    errors={errors}
                                    getIn={getIn}
                                    handleBlur={handleBlur}
                                />
                            </div>
                            <div className="flex flex-col items-center w-full py-5">
                                <p className="mb-3 text-center text-gray-700 font-medium text-sm max-w-xs leading-relaxed">
                                    Self-signing up? We’ll schedule an onboarding call to help you get started.
                                </p>
                                <div className="flex justify-center gap-4">
                                    <label htmlFor="selfOnboardYes" className="flex items-center text-gray-800 cursor-pointer">
                                        <input
                                            className="mr-2"
                                            type="radio"
                                            id="selfOnboardYes"
                                            name="selfOnboarding"
                                            value="true"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            checked={values.selfOnboarding === "true"}
                                            required
                                        />
                                        Yes
                                    </label>
                                    <label htmlFor="selfOnboardNo" className="flex items-center text-gray-800 cursor-pointer">
                                        <input
                                            className="mr-2"
                                            type="radio"
                                            id="selfOnboardNo"
                                            name="selfOnboarding"
                                            value="false"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            checked={values.selfOnboarding === "false"}
                                            required
                                        />
                                        No
                                    </label>
                                </div>
                            </div>
                            <TextBox
                                label={"Password"}
                                name={"password"}
                                type={"password"}
                                value={values.password}
                                onChange={handleChange}
                                errors={errors}
                                handleBlur={handleBlur}
                            />
                            {errors.password && touched.password ? (
                                <p className="text-[red]">{errors.password}</p>
                            ) : null}
                            {/* <ErrorMessage name="email" /> */}

                            <TextBox
                                label={"Confirm Password"}
                                name={"confirmPassword"}
                                type={"password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />

                            {values.password !== confirmPassword && (
                                <p className="text-[red]">Password does not match</p>
                            )}

                            <ReCAPTCHA ref={recaptchaRef} sitekey="6LfjWG8pAAAAAPaJUlEtOre0XeDd4K6I43dsBGTq" onChange={onCaptchaChange} onExpired={onCaptchaExpired} className="my-4 flex justify-center [&>div]:scale-90" />

                            <button
                                type="button"
                                onClick={() => handleSignUp()}
                                disabled={loading}
                                className="w-full flex justify-center items-center py-3 px-6 border border-transparent rounded-2xl text-base font-semibold text-white bg-gradient-to-r from-primary-500 via-purple-500 to-primary-600 hover:from-primary-600 hover:via-purple-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:-translate-y-1 shadow-2xl hover:shadow-primary-500/25 relative overflow-hidden group mt-8"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                {loading ? (
                                    <>
                                        <CircularProgress color="inherit" className="mr-3" size={24} />
                                        <span className="relative z-10">Creating account...</span>
                                    </>
                                ) : (
                                    <span className="relative z-10 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                        </svg>
                                        Sign Up to Bellhopt
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 text-center animate-fade-in-up">
                        <p className="text-sm text-gray-300">
                            By signing up, you agree to our{" "}
                            <Link to="/privacy" className="text-primary-300 hover:text-primary-200 transition-colors">Privacy Policy</Link>
                            {" "}and{" "}
                            <Link to="/terms" className="text-primary-300 hover:text-primary-200 transition-colors">Terms of Service</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupScreen;
